import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import EmbeddedCheckoutComponent from '@/components/EmbeddedCheckout';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import PublicLayout from '@/components/PublicLayout';
import SEOHead from '@/components/SEOHead';
import { categoryColors } from '@/data/articles';
import { useAllArticles } from '@/hooks/useAllArticles';
import { sup } from '@/lib/superscript';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useMemo, useEffect, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useArticleReadTime } from '@/hooks/useArticleReadTime';
import { getVisitorSource } from '@/lib/visitorSource';

// Redirect map for old/broken article URLs that previously returned 404.
// Sends visitors to the closest live article instead of a dead-end page.
const SLUG_REDIRECTS: Record<string, string> = {
  'why-so-many-people-fail-the-cii-r0-exams-and-how-to-avoid-becoming-one-of-them': 'why-people-fail-r03',
};

export default function InsightArticle() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { articles, loading: articlesLoading } = useAllArticles();

  // Handle legacy slug redirects (preserves SEO link equity by 301-style client redirect)
  useEffect(() => {
    if (slug && SLUG_REDIRECTS[slug]) {
      navigate(`/insights/${SLUG_REDIRECTS[slug]}`, { replace: true });
    }
  }, [slug, navigate]);

  const article = articles.find((a) => a.slug === slug);

  const articleJsonLd = useMemo(() => article ? ({
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.metaDescription,
    datePublished: new Date(article.date).toISOString().split('T')[0],
    dateModified: new Date(article.date).toISOString().split('T')[0],
    image: article.heroImageUrl
      ? (article.heroImageUrl.startsWith('http') ? article.heroImageUrl : `https://www.r0hub.com${article.heroImageUrl}`)
      : 'https://www.r0hub.com/og-image.jpg',
    author: { '@type': 'Organization', name: 'R0 Hub', url: 'https://www.r0hub.com' },
    publisher: { '@type': 'Organization', name: 'R0 Hub', url: 'https://www.r0hub.com', logo: { '@type': 'ImageObject', url: 'https://www.r0hub.com/favicon.png' } },
    mainEntityOfPage: `https://www.r0hub.com/insights/${article.slug}`,
  }) : undefined, [article]);

  const faqJsonLd = useMemo(() => {
    if (!article?.faqSchema?.length) return undefined;
    return {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: article.faqSchema.map((f) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: { '@type': 'Answer', text: f.answer },
      })),
    };
  }, [article]);

  const [heroOk, setHeroOk] = useState(true);
  const [infoOk, setInfoOk] = useState(true);
  const [workbookCheckoutOpen, setWorkbookCheckoutOpen] = useState(false);
  useEffect(() => { setHeroOk(true); setInfoOk(true); }, [article?.slug]);

  // Track article view (once per page load) via edge function (captures IP + user)
  const tracked = useRef(false);
  useEffect(() => {
    if (!article || tracked.current) return;
    tracked.current = true;
    supabase.functions.invoke('track-content-view', {
      body: { content_type: 'article', content_slug: article.slug, ...getVisitorSource() },
    }).catch(() => {});
  }, [article]);

  // Track active read time (heartbeats every 10s while tab is visible/focused)
  useArticleReadTime(article?.slug);

  const otherArticles = articles.filter((a) => a.slug !== slug).slice(0, 4);

  if (!article) {
    if (articlesLoading) {
      return (
        <PublicLayout>
          <div className="container py-24 text-center text-muted-foreground">Loading…</div>
        </PublicLayout>
      );
    }
    return (
      <PublicLayout>
        <div className="container py-24 text-center">
          <h1 className="text-2xl font-bold text-foreground mb-4">Article not found</h1>
          <Button variant="outline" onClick={() => navigate('/insights')}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Insights
          </Button>
        </div>
      </PublicLayout>
    );
  }

  const Icon = article.icon;
  const markdownComponents: Components = {
    p: ({ children }) => (
      <p className="mb-6 text-foreground/80 leading-8 last:mb-0">{children}</p>
    ),
    ul: ({ children }) => (
      <ul className="!list-disc !pl-6 mb-6 mt-3 space-y-3 text-foreground/80 leading-7">{children}</ul>
    ),
    ol: ({ children }) => (
      <ol className="!list-decimal !pl-6 mb-6 mt-3 space-y-3 text-foreground/80 leading-7">{children}</ol>
    ),
    li: ({ children }) => (
      <li className="pl-1.5 leading-7">{children}</li>
    ),
    h2: ({ children }) => (
      <h2 className="text-xl font-bold text-foreground font-display mt-12 mb-5">{children}</h2>
    ),
    h3: ({ children }) => (
      <h3 className="text-lg font-bold text-foreground font-display mt-9 mb-4">{children}</h3>
    ),
    hr: () => (
      <hr className="border-border/40 my-12" />
    ),
    strong: ({ children }) => (
      <strong className="text-foreground font-semibold">{children}</strong>
    ),
    a: ({ children, href }) => {
      if (href?.startsWith('/register?checkout=')) {
        return (
          <span className="block text-center my-8">
            <a
              href={href}
              className="inline-block px-8 py-4 rounded-xl gradient-gold text-accent-foreground font-semibold shadow-gold hover:opacity-90 transition-opacity"
            >
              {children}
            </a>
          </span>
        );
      }
      return (
        <a href={href} className="text-accent hover:underline">
          {children}
        </a>
      );
    },
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-accent/40 pl-4 my-6 italic text-foreground/70">{children}</blockquote>
    ),
  };

  return (
    <PublicLayout>
      <SEOHead
        title={article.metaTitle ? `${article.metaTitle} | R0 Hub` : `${article.title} | R0 Hub`}
        description={article.metaDescription}
        canonical={`https://www.r0hub.com/insights/${article.slug}`}
        type="article"
        keywords={`CII exam, ${article.category}, ${article.title.split(':')[0]}, CII revision, financial services exam`}
        jsonLd={articleJsonLd}
        jsonLdExtra={faqJsonLd ? [faqJsonLd] : undefined}
        breadcrumbs={[
          { name: 'Home', url: 'https://www.r0hub.com/' },
          { name: 'Insights', url: 'https://www.r0hub.com/insights' },
          { name: article.title, url: `https://www.r0hub.com/insights/${article.slug}` },
        ]}
      />

      {/* Header */}
      <section className="py-12 sm:py-16 hero-pattern">
        <div className="container max-w-3xl">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/insights')}
              className="text-primary-foreground/70 hover:text-primary-foreground mb-6 -ml-2"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> All Insights
            </Button>

            <Badge
              variant="outline"
              className={`mb-4 text-xs font-medium ${categoryColors[article.category] || ''}`}
            >
              {article.category}
            </Badge>

            <h1 className="text-3xl sm:text-4xl font-bold text-primary-foreground font-display mb-4 leading-tight">
              {article.title}
            </h1>

            <div className="flex items-center gap-4 text-sm text-primary-foreground/60">
              <span className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" />
                {article.readTime}
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Body */}
      <section className="py-10 sm:py-14 bg-background">
        <div className="container max-w-3xl">
          {article.heroImageUrl && heroOk && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="mb-8 -mt-2 overflow-hidden rounded-xl border border-border/40 bg-muted/20"
            >
              <img
                src={article.heroImageUrl}
                alt={article.heroImageAlt ?? article.title}
                onError={() => setHeroOk(false)}
                className="w-full h-auto max-h-[400px] object-cover"
                loading="eager"
              />
            </motion.div>
          )}

          <div className={article.heroImageUrl && heroOk ? '-mt-4 mb-8' : 'mb-8 -mt-2'}>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                navigate(`/ask?q=${encodeURIComponent(`I'd like to ask about the R0 Hub article "${article.title}". `)}`)
              }
              className="gap-1.5 border-accent/30 text-accent hover:bg-accent/5"
            >
              <Sparkles className="w-4 h-4" />
              Ask Rio about this
            </Button>
          </div>

          <motion.article
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="prose prose-lg max-w-none
              prose-headings:font-display prose-headings:text-foreground prose-headings:font-bold
              prose-h2:text-xl prose-h2:mt-8 prose-h2:mb-3
              prose-h3:text-lg prose-h3:mt-6 prose-h3:mb-2
              prose-li:text-foreground/80
              prose-strong:text-foreground
              prose-hr:border-border/40 prose-hr:my-8
              prose-ol:text-foreground/80 prose-ul:text-foreground/80
              prose-a:text-accent
              [&_img]:rounded-lg [&_img]:max-w-full [&_img]:h-auto [&_img]:mx-auto
              [&_blockquote]:border-l-4 [&_blockquote]:border-accent/40 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-foreground/70
              [&_pre]:bg-foreground [&_pre]:text-background [&_pre]:p-4 [&_pre]:rounded-lg [&_pre]:overflow-x-auto
              [&_code]:bg-muted [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-[0.95em]
              [&_pre_code]:bg-transparent [&_pre_code]:p-0
              [&_table]:w-full [&_table]:border-collapse [&_th]:border [&_th]:border-border/60 [&_th]:p-2 [&_th]:bg-muted/40 [&_td]:border [&_td]:border-border/60 [&_td]:p-2
              [&_tr:nth-child(even)]:bg-muted/20"
          >
            {article.isHtml ? (
              <div dangerouslySetInnerHTML={{ __html: article.content }} />
            ) : (
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{sup(article.content)}</ReactMarkdown>
            )}
          </motion.article>

          {article.slug === 'r02-key-formulas' && (
            <div className="mt-10 p-6 rounded-xl border border-accent/20 bg-accent/5 text-center">
              <p className="text-foreground/90 text-base leading-relaxed mb-4">
                Purchase our <strong className="text-foreground">R02 Calculation Workbook</strong> which covers every formula in the R02 syllabus. Includes 5 questions per formula with worked examples.
              </p>
              <Button
                onClick={() => setWorkbookCheckoutOpen(true)}
                className="gradient-navy text-primary-foreground hover:opacity-90 font-semibold"
              >
                Purchase Workbook
              </Button>
            </div>
          )}

          {article.infographicUrl && infoOk && (
            <div className="mt-10 overflow-hidden rounded-xl border border-border/40">
              <img
                src={article.infographicUrl}
                alt={`${article.title} infographic`}
                onError={() => setInfoOk(false)}
                className="w-full h-auto"
                loading="lazy"
              />
            </div>
          )}

          {article.faqSchema && article.faqSchema.length > 0 && (
            <div className="mt-12 pt-8 border-t border-border/40">
              <h2 className="text-2xl font-bold text-foreground font-display mb-6">Frequently Asked Questions</h2>
              <Accordion type="single" collapsible defaultValue="faq-0" className="w-full">
                {article.faqSchema.map((f, i) => (
                  <AccordionItem key={i} value={`faq-${i}`}>
                    <AccordionTrigger className="text-left text-base font-semibold text-foreground">{f.question}</AccordionTrigger>
                    <AccordionContent className="text-foreground/80 leading-7">{f.answer}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          )}

          {/* Internal Links */}
          <div className="mt-10 pt-6 border-t border-border/40">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Related Resources</h3>
            <div className="flex flex-wrap gap-2">
              {['R01', 'R02', 'R03', 'R04', 'R05', 'R06'].map((code) => (
                <a
                  key={code}
                  href={`/${code.toLowerCase()}-exam-practice`}
                  onClick={(e) => { e.preventDefault(); navigate(`/${code.toLowerCase()}-exam-practice`); }}
                  className="text-sm text-accent hover:underline bg-accent/5 px-3 py-1.5 rounded-lg border border-accent/20"
                >
                  {code} exam practice questions
                </a>
              ))}
            </div>
          </div>

          {/* More Articles */}
          {otherArticles.length > 0 && (
            <div className="mt-8 pt-6 border-t border-border/40">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">More from R0 Hub Insights</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {otherArticles.map((a) => (
                  <a
                    key={a.slug}
                    href={`/insights/${a.slug}`}
                    onClick={(e) => { e.preventDefault(); navigate(`/insights/${a.slug}`); }}
                    className="text-sm text-foreground hover:text-accent transition-colors p-3 rounded-lg border border-border/40 hover:border-accent/30"
                  >
                    {a.title}
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="mt-12 pt-8 border-t border-border/40 text-center">
            <h3 className="text-xl font-bold text-foreground font-display mb-2">
              Ready to test your knowledge?
            </h3>
            <p className="text-muted-foreground text-sm mb-5">
              Put what you've learned into practice with exam-style questions
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                asChild
                className="gradient-navy text-primary-foreground hover:opacity-90 font-semibold"
              >
                <Link to="/register">Start Practising Free</Link>
              </Button>
              <Button
                variant="outline"
                asChild
                className="border-border/60 text-muted-foreground hover:text-foreground"
              >
                <Link to="/insights">More Articles</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {workbookCheckoutOpen && (
        <EmbeddedCheckoutComponent
          moduleCode="R02_WORKBOOK"
          onClose={() => setWorkbookCheckoutOpen(false)}
        />
      )}
    </PublicLayout>
  );
}
